
// src/features/video/pages/VideoPage.jsx
import { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo } from "react";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Accordion } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import { useAuth } from "../../../AuthUser";
import Vimeo from "@u-wave/react-vimeo";
import API from "../../../api";

import { useAuthModal } from "../../Auth/useAuthModal";
import useAuthReady from "../../Auth/useAuthReady";

import Description from "../components/Description";
import Sidebar from "../components/Sidebar";
import CommentsSection from "../components/CommentsSection";

import useVideoData from "../hooks/useVideoData";
import useProgress from "../hooks/useProgress";
import useInfiniteScroll from "../hooks/useInfiniteScroll";
import useResponsive from "../hooks/useResponsive";

import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import "../styles/video.css";

/* Modern overlay identical to cards; main player variant centers and is clickable */
function ModernSubscriptionOverlay({ onClick, variant = "default" }) {
  const className = variant === "main" ? "lock-modern lock-main" : "lock-modern";
  return (
      <div className={className} onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}>
        <div className="lock-badge">
          <i className="fa-solid fa-lock" />
        </div>
        <div className="lock-hint">Subscribe to watch</div>
      </div>
  );
}

function VideoPage() {
  const auth = useAuth();
  const { slug } = useParams();
  const navigate = useNavigate();

  const authReady = useAuthReady();
  const { openAuthModal } = useAuthModal();

  const { video, categoryVideos, isLoading, setCategoryVideos, setVideo } = useVideoData();
  const { isMobileLike, observerSupported } = useResponsive();

  const [bootstrapping, setBootstrapping] = useState(true);
  const [thumbOk, setThumbOk] = useState(false);
  const [thumbErr, setThumbErr] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);

  const vimeoFallbackTimer = useRef(null);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  const {
    visibleCount,
    isPaging,
    hasMore,
    loadMore,
    resetVisible,
    isScrollable,
    recalcScrollable,
  } = useInfiniteScroll({
    listGetter: () => categoryVideos[0]?.videos || [],
    rootRef: scrollRef,
    sentinelRef,
  });

  const {
    vimeoPlayerRef,
    lastSavedSecondRef,
    progressMap,
    refreshProgressMapFromStorage,
    handleVimeoReady: progressOnReady,
    handleVimeoTimeUpdate,
    handleVimeoEnd,
  } = useProgress({ slug, video, categoryVideos });

  const fetchData = useCallback(
      async (theSlug) => {
        const res = await API.get(`video/${theSlug}`);
        setVideo(res.data);
        const cat = await API.get(`video/category/${res.data.categoryId}`);
        setCategoryVideos(cat.data);
      },
      [setVideo, setCategoryVideos]
  );

  // Load per slug
  useLayoutEffect(() => {
    (async () => {
      if (!slug) return;
      setBootstrapping(true);
      setThumbOk(false);
      setThumbErr(false);
      setPlayerReady(false);
      if (vimeoFallbackTimer.current) {
        clearTimeout(vimeoFallbackTimer.current);
        vimeoFallbackTimer.current = null;
      }
      await fetchData(slug);
    })();
  }, [slug, fetchData]);

  // Auth modal once per slug
  const promptedRef = useRef(false);
  useEffect(() => {
    if (!promptedRef.current && authReady && !auth?.user) {
      promptedRef.current = true;
      openAuthModal({
        onSuccess: async () => {
          if (slug) await fetchData(slug);
        },
      });
    }
  }, [authReady, auth?.user, openAuthModal, fetchData, slug]);

  useEffect(() => {
    promptedRef.current = false;
  }, [slug]);

  useEffect(() => {
    resetVisible();
  }, [slug, resetVisible]);

  useEffect(() => {
    refreshProgressMapFromStorage();
  }, [categoryVideos, visibleCount, refreshProgressMapFromStorage]);

  useEffect(() => {
    recalcScrollable();
  }, [categoryVideos, visibleCount, recalcScrollable]);

  // Finish bootstrapping after auth is decided and first data present
  useEffect(() => {
    if (authReady && (video?.id || isLoading === false)) {
      const t = setTimeout(() => setBootstrapping(false), 40);
      return () => clearTimeout(t);
    }
  }, [authReady, video?.id, isLoading]);

  const vimeoId = video?.vimeoId || "";
  const title = video?.title || "";
  const thumbnail = video?.thumb || video?.thumbnail || "";
  const description = video?.description || "";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: title || "MyGolfDNA Video",
    description:
        (description?.replaceAll(/<[^>]+>/g, "").slice(0, 500)) ||
        "Golf instruction video on MyGolfDNA.",
    thumbnailUrl: thumbnail ? [thumbnail] : undefined,
    embedUrl: `https://player.vimeo.com/video/${video?.vimeoId}`,
    uploadDate: video?.created_at || undefined,
    duration: video?.durationSeconds ? `PT${Math.round(video.durationSeconds)}S` : undefined,
    publisher: {
      "@type": "Organization",
      name: "MyGolfDNA",
      logo: { "@type": "ImageObject", url: "https://static.mygolfdna.com/logo.png" },
    },
  };

  // ---- DERIVED FLAGS ----
  const hasVideoMeta = !!video?.id && typeof video?.free === "boolean";
  const entitled = !!auth?.user && (auth.user.planType || video?.free);
  const needThumb =
      !auth?.user ||
      (!!auth?.user && !auth.user.planType && (hasVideoMeta ? !video.free : false));
  const overlayVisible =
      authReady && hasVideoMeta && !!auth?.user && !auth.user.planType && !video.free;
  // -----------------------

  // Reset readiness when entitlement/needThumb changes
  useEffect(() => {
    setPlayerReady(false);
    setThumbOk(false);
    setThumbErr(false);
    if (vimeoFallbackTimer.current) {
      clearTimeout(vimeoFallbackTimer.current);
      vimeoFallbackTimer.current = null;
    }
  }, [entitled, needThumb, slug]);

  const handleThumbLoad = () => {
    setThumbOk(true);
    if (needThumb) setPlayerReady(true);
  };
  const handleThumbError = () => {
    setThumbErr(true);
    if (needThumb) setPlayerReady(true);
  };

  const handleVimeoReady = (player) => {
    progressOnReady(player);
    setPlayerReady(true);
    if (vimeoFallbackTimer.current) {
      clearTimeout(vimeoFallbackTimer.current);
      vimeoFallbackTimer.current = null;
    }
  };

  // Fallback if Vimeo onReady is slow
  useEffect(() => {
    if (entitled && video?.vimeoId && !playerReady) {
      vimeoFallbackTimer.current = setTimeout(() => setPlayerReady(true), 1200);
      return () => {
        if (vimeoFallbackTimer.current) clearTimeout(vimeoFallbackTimer.current);
      };
    }
  }, [entitled, video?.vimeoId, playerReady]);

  const pageSkeleton = bootstrapping || isLoading;
  const invalidSlug = !slug;

  if (invalidSlug) {
    return <Navigate to="/" replace />;
  }

  const goPricing = () => navigate("/pricing");

  return (
      <>
        <Helmet>
          <title>{title ? `${title} | MyGolfDNA.com` : "MyGolfDNA.com"}</title>
          <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
          {thumbnail ? <link rel="preload" as="image" href={thumbnail} /> : null}
        </Helmet>

        <SkeletonTheme baseColor="#d9d9d9" highlightColor="#efefef">
          <div className="nav-spacer" />

          <div className="video-page mobile-anti-jank py-4" style={{ "--page-pad": "12px" }}>
            <div className="video-max">
              <div className="yt-layout">
                <div className="yt-main">
                  {/* PLAYER */}
                  <div className="mt-3 mt-sm-4 video-player-shell edge-cancel-mobile">
                    <div className={`player-frame ${playerReady ? "player-ready" : ""}`}>
                      {!playerReady ? (
                          <div className="player-skeleton absolute-fill">
                            {thumbnail ? (
                                <img src={thumbnail} alt="" className="blur-skeleton" decoding="async" />
                            ) : (
                                <div className="thumb-fallback absolute-fill" />
                            )}
                            {overlayVisible ? (
                                <div className="absolute-fill overlay-layer in-main-player">
                                  <ModernSubscriptionOverlay variant="main" onClick={goPricing} />
                                </div>
                            ) : null}
                          </div>
                      ) : (
                          <>
                            {needThumb ? (
                                <>
                                  {thumbnail ? (
                                      <img
                                          src={thumbnail}
                                          alt={title}
                                          className="thumb"
                                          onLoad={thumbOk ? undefined : handleThumbLoad}
                                          onError={thumbErr ? undefined : handleThumbError}
                                          decoding="async"
                                          loading="eager"
                                      />
                                  ) : (
                                      <div className="thumb-fallback absolute-fill" />
                                  )}
                                  {overlayVisible ? (
                                      <div className="absolute-fill overlay-layer in-main-player">
                                        <ModernSubscriptionOverlay variant="main" onClick={goPricing} />
                                      </div>
                                  ) : null}
                                </>
                            ) : vimeoId ? (
                                <div className="absolute-fill vimeo-wrap">
                                  <Vimeo
                                      key={vimeoId}
                                      video={vimeoId}
                                      autoplay
                                      playsInline
                                      responsive={false}
                                      width="100%"
                                      height="100%"
                                      onReady={handleVimeoReady}
                                      onTimeUpdate={handleVimeoTimeUpdate}
                                      onEnd={handleVimeoEnd}
                                      onError={() => {}}
                                  />
                                </div>
                            ) : thumbnail ? (
                                <img
                                    src={thumbnail}
                                    alt={title}
                                    className="thumb"
                                    onLoad={thumbOk ? undefined : handleThumbLoad}
                                    onError={thumbErr ? undefined : handleThumbError}
                                    decoding="async"
                                    loading="eager"
                                />
                            ) : (
                                <div className="thumb-fallback absolute-fill" />
                            )}
                          </>
                      )}
                    </div>
                  </div>

                  {/* TITLE + DESCRIPTION */}
                  <div className="mt-2 pt-2">
                    {pageSkeleton ? (
                        <>
                          <Skeleton height={18} width="66%" />
                          <div className="mt-2" />
                          <Skeleton height={14} width="90%" />
                          <div className="mt-2" />
                          <Skeleton height={14} width="80%" />
                        </>
                    ) : (
                        <>
                          <h4>{title}</h4>
                          <Description html={video?.description} />
                        </>
                    )}

                    <Accordion defaultActiveKey="0" className="pt-4">
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>Comments</Accordion.Header>
                        <Accordion.Body>
                          <CommentsSection />
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </div>
                </div>

                {/* SIDEBAR (no hint/pulse on small thumbs, no desktop hover) */}
                <aside className="yt-side no-desktop-hover-preview">
                  <Sidebar
                      auth={auth}
                      slug={slug}
                      scrollRef={scrollRef}
                      sentinelRef={sentinelRef}
                      videos={categoryVideos[0]?.videos || []}
                      visibleCount={visibleCount}
                      progressMap={progressMap}
                      isPaging={isPaging}
                      hasMore={hasMore}
                      loadMore={loadMore}
                      isMobileLike={isMobileLike}
                      observerSupported={observerSupported}
                      isScrollable={isScrollable}
                  />
                </aside>
              </div>
            </div>
          </div>
        </SkeletonTheme>

        <ToastContainer />
      </>
  );
}

export default VideoPage;
